import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, Row, Col, Card, Button, Spinner, Badge } from "react-bootstrap";
import { FaTimes, FaPlus } from "react-icons/fa";
import api from "../../../api";
import swal from "sweetalert";

// Small helper: renders a tag-style multi-email input 
const MultiEmailInput = ({ label, emails, onAdd, onRemove, allClientEmails, loadingClients, placeholder }) => {
  const [inputVal, setInputVal] = useState("");


  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && inputVal.trim()) {
      e.preventDefault();
      const val = inputVal.trim().replace(/,$/, "");
      if (val && !emails.includes(val)) onAdd(val);
      setInputVal("");
    }
  };

  const handleBlurAdd = () => {
    const val = inputVal.trim();
    if (val && !emails.includes(val)) onAdd(val);
    setInputVal("");
  };

  return (
    <Form.Group className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <Form.Label className="mb-0 fs-13 font-w600 text-dark">{label}</Form.Label>
      </div>

      {/* Tag display of selected emails */}
      {emails.length > 0 && (
        <div
          className="d-flex flex-wrap gap-1 p-2 mb-2 border rounded"
          style={{ background: "#ffffff", minHeight: "38px" }}
        >
          {emails.map((email, idx) => (
            <Badge
              key={idx}
              bg="primary"
              className="d-flex align-items-center gap-1 px-2 py-1 fs-12 font-w500"
            >
              {email}
              <FaTimes
                style={{ cursor: "pointer", fontSize: "10px" }}
                onClick={() => onRemove(email)}
                title="Remove"
              />
            </Badge>
          ))}
        </div>
      )}

      <div className="d-flex gap-2">
        {/* Dropdown from client list */}
        <Form.Select
          onChange={(e) => {
            const val = e.target.value;
            if (val === "__ALL__") {
              allClientEmails.forEach((item) => {
                if (!emails.includes(item.email)) onAdd(item.email);
              });
            } else if (val && !emails.includes(val)) {
              onAdd(val);
            }
            e.target.value = "";
          }}
          disabled={loadingClients}
          style={{ maxWidth: "50%" }}
          defaultValue=""
        >
          <option value="">-- Select Saved Client Contact --</option>
          {allClientEmails.length > 1 && (
            <option value="__ALL__" className="fw-bold text-primary">
              ✓ Select All Saved Contacts ({allClientEmails.length})
            </option>
          )}
          {allClientEmails.map((item, idx) => {
            const isSelected = emails.includes(item.email);
            return (
              <option key={idx} value={item.email}>
                {isSelected ? "✓ " : ""}{item.label} ({item.email})
              </option>
            );
          })}
        </Form.Select>

        {/* Manual text input */}
        <div className="d-flex flex-grow-1">
          <Form.Control
            type="email"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlurAdd}
            placeholder={placeholder || "Type additional email & press Enter"}
          />
          <Button
            variant="outline-secondary"
            className="ms-1"
            onClick={handleBlurAdd}
            title="Add"
            disabled={!inputVal.trim()}
          >
            <FaPlus />
          </Button>
        </div>
      </div>
      <Form.Text className="text-muted fs-11">
        Click saved contact badges above or type any additional email and press Enter / comma.
      </Form.Text>
    </Form.Group>
  );
};
// 

const EmailModal = ({ show, onHide, initialData = {} }) => {
 const [templates, setTemplates] = useState([]);
 const [clients, setClients] = useState([]);

 // to / cc / bcc are now arrays of email strings
 const [toEmails, setToEmails] = useState([]);
 const [ccEmails, setCcEmails] = useState([]);
 const [bccEmails, setBccEmails] = useState([]);

 const [showCc, setShowCc] = useState(false);
 const [showBcc, setShowBcc] = useState(false);

 const [emailForm, setEmailForm] = useState({
 subject: "",
 body: "",
 templateId: "",
 date: new Date().toISOString().split("T")[0],
 time: new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
 });
 const [placeholderValues, setPlaceholderValues] = useState({});
 const [showCompensation, setShowCompensation] = useState(true);
 const [sendingEmail, setSendingEmail] = useState(false);
 const [loadingTemplates, setLoadingTemplates] = useState(false);
 const [loadingClients, setLoadingClients] = useState(false);

 useEffect(() => {
 if (show) {
 fetchTemplates();
 fetchClients();
 // Initialize To from initialData
 const initTo = initialData.to ? [initialData.to] : [];
 setToEmails(initTo);
 setCcEmails([]);
 setBccEmails([]);
 setShowCc(false);
 setShowBcc(false);
 setEmailForm({
 subject: initialData.subject || "",
 body: initialData.body || "",
 templateId: "",
 date: new Date().toISOString().split("T")[0],
 time: new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
 });
 setPlaceholderValues(initialData.placeholders || {});
 setShowCompensation(true);
 }
 }, [show, initialData]);

 const fetchTemplates = async () => {
 try {
 setLoadingTemplates(true);
 const res = await api.get("/emails/templates");
 if (res.data.success) setTemplates(res.data.data || []);
 } catch (err) {
 console.error("Error fetching templates:", err);
 } finally {
 setLoadingTemplates(false);
 }
 };

 const fetchClients = async () => {
 try {
 setLoadingClients(true);
 const res = await api.get("/clients?limit=1000");
 if (res.data.success) setClients(res.data.data || []);
 } catch (err) {
 console.error("Error fetching clients:", err);
 } finally {
 setLoadingClients(false);
 }
 };

 const allClientEmails = useMemo(() => {
  const targetClientId =
    (initialData.clientId && typeof initialData.clientId === "object"
      ? initialData.clientId._id
      : initialData.clientId) ||
    (initialData.client && typeof initialData.client === "object"
      ? initialData.client._id
      : initialData.client);

  const targetClientName =
    initialData.placeholders?.clientName ||
    initialData.clientName ||
    (typeof initialData.client === "string" ? initialData.client : "");

  let targetClients = [];
  if (targetClientId) {
    targetClients = clients.filter((c) => String(c._id) === String(targetClientId));
  }
  if (targetClients.length === 0 && targetClientName && targetClientName !== "N/A") {
    const clean = targetClientName.trim().toLowerCase();
    targetClients = clients.filter(
      (c) =>
        (c.name && c.name.trim().toLowerCase().includes(clean)) ||
        (c.clientName && c.clientName.trim().toLowerCase().includes(clean)) ||
        clean.includes((c.name || "").trim().toLowerCase())
    );
  }

  // Only fallback to all clients if there is no specific client context at all
  if (targetClients.length === 0 && !targetClientId && !targetClientName) {
    targetClients = clients;
  }
  const emailList = [];
  targetClients.forEach((client) => {
    if (client.gmail) {
      emailList.push({ email: client.gmail.toLowerCase(), label: `${client.name || "Client"} (Main)` });
    } else if (client.email) {
      emailList.push({ email: client.email.toLowerCase(), label: `${client.name || "Client"} (Email)` });
    } else if (client.companyDetails?.companyEmail) {
      emailList.push({ email: client.companyDetails.companyEmail.toLowerCase(), label: `${client.name || "Client"} (Company)` });
    }
    const contacts = Array.isArray(client.contactPersons)
      ? client.contactPersons
      : client.contactPerson
      ? [client.contactPerson]
      : [];
    contacts.forEach((cp) => {
      if (cp.email) {
        emailList.push({
          email: cp.email.toLowerCase(),
          label: `${cp.name || "Contact"}${cp.designation ? ` (${cp.designation})` : ` (${client.name || "Client"})`}`,
        });
      }
    });
  });

  const uniqueMap = new Map();
  emailList.forEach((item) => {
    if (!uniqueMap.has(item.email)) {
      uniqueMap.set(item.email, item);
    }
  });
  return Array.from(uniqueMap.values());
 }, [clients, initialData.clientId, initialData.client, initialData.clientName, initialData.placeholders]);

 // Generic add/remove helpers
 const addEmail = (setter) => (email) => setter((prev) => (prev.includes(email) ? prev : [...prev, email]));
 const removeEmail = (setter) => (email) => setter((prev) => prev.filter((e) => e !== email));

 const handleTemplateChange = (e) => {
 const id = e.target.value;
 const selected = templates.find((t) => t._id === id);
 if (selected) {
 const bodyMatches = selected.body.match(/{{(.*?)}}/g) || [];
 const subjectMatches = selected.subject.match(/{{(.*?)}}/g) || [];
 const allMatches = [...new Set([...bodyMatches, ...subjectMatches])];
 const newPlaceholders = { ...placeholderValues };
 allMatches.forEach((m) => {
 const key = m.replace(/{{|}}/g, "");
 if (!newPlaceholders[key]) newPlaceholders[key] = "";
 });
 setPlaceholderValues(newPlaceholders);
 setEmailForm((prev) => ({ ...prev, templateId: id, subject: selected.subject, body: selected.body }));
 } else {
 setEmailForm((prev) => ({ ...prev, templateId: "", body: "" }));
 }
 };

 const handlePlaceholderChange = (key, value) => {
 setPlaceholderValues((prev) => ({ ...prev, [key]: value }));
 };

 const finalEmailBody = useMemo(() => {
 let content = emailForm.body;
 if (!content) return "";
 Object.keys(placeholderValues).forEach((key) => {
 const regex = new RegExp(`{{${key}}}`, "g");
 content = content.replace(regex, placeholderValues[key] || `[${key}]`);
 });
 const selectedTemplate = templates.find((t) => t._id === emailForm.templateId);
 if (selectedTemplate?.type === "Payment" && !showCompensation) {
 content = content.replace(/<!-- COMPENSATION_START -->[\s\S]*?<!-- COMPENSATION_END -->/, "");
 }
 return content;
 }, [emailForm.body, placeholderValues, showCompensation, emailForm.templateId, templates]);

 const handleSendEmail = async () => {
 if (toEmails.length === 0 || !emailForm.subject || !finalEmailBody) {
 swal("Error", "Please fill in all required fields (To, Subject, Body)", "error");
 return;
 }

 setSendingEmail(true);
 try {
 let finalSubject = emailForm.subject;
 Object.keys(placeholderValues).forEach((key) => {
 const regex = new RegExp(`{{${key}}}`, "g");
 finalSubject = finalSubject.replace(regex, placeholderValues[key] || `[${key}]`);
 });

 const res = await api.post("/emails/send", {
 to: toEmails,
 cc: ccEmails.length > 0 ? ccEmails : undefined,
 bcc: bccEmails.length > 0 ? bccEmails : undefined,
 subject: finalSubject,
 body: finalEmailBody,
 date: emailForm.date,
 time: emailForm.time,
 });

 if (res.data.success) {
 swal("Success", "Email sent successfully", "success");
 onHide();
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to send email", "error");
 } finally {
 setSendingEmail(false);
 }
 };

 return (
 <Modal show={show} onHide={onHide} size="lg">
 <Modal.Header closeButton>
 <Modal.Title>Send Email</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <Form>
 <Row>
 {/* Template selector */}
 <Col md={12}>
 <Form.Group className="mb-3">
 <Form.Label>Select Template</Form.Label>
 <Form.Select
 value={emailForm.templateId}
 onChange={handleTemplateChange}
 disabled={loadingTemplates}
 >
 <option value="">-- Custom Email --</option>
 {templates.map((t) => (
 <option key={t._id} value={t._id}>
 {t.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 {/* Template placeholder fields */}
 {emailForm.templateId && Object.keys(placeholderValues).length > 0 && (
 <Col md={12}>
 <Card className="mb-3 border-info shadow-none">
 <Card.Body className="p-3">
 <h6 className="mb-3 text-info">Fill Template Details</h6>
 <Row>
 {Object.keys(placeholderValues).map((key) => (
 <Col md={6} key={key} className="mb-2">
 <Form.Group>
 <Form.Label className="small text-muted text-capitalize">
 {key.replace(/([A-Z])/g, " $1")}
 </Form.Label>
 <Form.Control
 size="sm"
 type={key.toLowerCase().includes("date") ? "date" : "text"}
 value={placeholderValues[key]}
 onChange={(e) => handlePlaceholderChange(key, e.target.value)}
 placeholder={`Enter ${key}...`}
 />
 </Form.Group>
 </Col>
 ))}
 {templates.find((t) => t._id === emailForm.templateId)?.type === "Payment" && (
 <Col md={12} className="mt-2">
 <Form.Check
 type="switch"
 id="compensation-switch"
 label="Include Compensation/Escalation Section"
 checked={showCompensation}
 onChange={(e) => setShowCompensation(e.target.checked)}
 />
 </Col>
 )}
 </Row>
 </Card.Body>
 </Card>
 </Col>
 )}

 {/* TO — multiple recipients */}
 <Col md={12}>
 <div className="d-flex justify-content-between align-items-center mb-1">
 <Form.Label className="mb-0 fw-semibold">To <span className="text-danger">*</span></Form.Label>
 <div className="d-flex gap-2">
 {!showCc && (
 <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => setShowCc(true)}>
 + CC
 </Button>
 )}
 {!showBcc && (
 <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => setShowBcc(true)}>
 + BCC
 </Button>
 )}
 </div>
 </div>
 <MultiEmailInput
 label=""
 emails={toEmails}
 onAdd={addEmail(setToEmails)}
 onRemove={removeEmail(setToEmails)}
 allClientEmails={allClientEmails}
 loadingClients={loadingClients}
 placeholder="Type email & press Enter to add"
 />
 </Col>

 {/* CC */}
 {showCc && (
 <Col md={12}>
 <div className="d-flex justify-content-between align-items-center mb-1">
 <Form.Label className="mb-0 fw-semibold">CC</Form.Label>
 <Button
 variant="link"
 size="sm"
 className="p-0 text-danger text-decoration-none"
 onClick={() => { setShowCc(false); setCcEmails([]); }}
 >
 Remove CC
 </Button>
 </div>
 <MultiEmailInput
 label=""
 emails={ccEmails}
 onAdd={addEmail(setCcEmails)}
 onRemove={removeEmail(setCcEmails)}
 allClientEmails={allClientEmails}
 loadingClients={loadingClients}
 placeholder="Add CC recipients"
 />
 </Col>
 )}

 {/* BCC */}
 {showBcc && (
 <Col md={12}>
 <div className="d-flex justify-content-between align-items-center mb-1">
 <Form.Label className="mb-0 fw-semibold">BCC</Form.Label>
 <Button
 variant="link"
 size="sm"
 className="p-0 text-danger text-decoration-none"
 onClick={() => { setShowBcc(false); setBccEmails([]); }}
 >
 Remove BCC
 </Button>
 </div>
 <MultiEmailInput
 label=""
 emails={bccEmails}
 onAdd={addEmail(setBccEmails)}
 onRemove={removeEmail(setBccEmails)}
 allClientEmails={allClientEmails}
 loadingClients={loadingClients}
 placeholder="Add BCC recipients"
 />
 </Col>
 )}

 {/* Date & Time */}
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Date</Form.Label>
 <Form.Control
 type="date"
 value={emailForm.date}
 onChange={(e) => setEmailForm({ ...emailForm, date: e.target.value })}
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>Time</Form.Label>
 <Form.Control
 type="time"
 value={emailForm.time}
 onChange={(e) => setEmailForm({ ...emailForm, time: e.target.value })}
 />
 </Form.Group>
 </Col>

 {/* Subject */}
 <Col md={12}>
 <Form.Group className="mb-3">
 <Form.Label>Subject</Form.Label>
 <Form.Control
 type="text"
 value={emailForm.subject}
 onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
 placeholder="Email subject"
 />
 </Form.Group>
 </Col>

 {/* Body */}
 <Col md={12}>
 <Form.Group className="mb-3">
 <Form.Label>Email Body (HTML supported)</Form.Label>
 <div
 className="border rounded p-3 mb-2"
 style={{ minHeight: "150px", maxHeight: "300px", overflowY: "auto", fontSize: "14px" }}
 dangerouslySetInnerHTML={{ __html: finalEmailBody || emailForm.body }}
 />
 <Form.Control
 as="textarea"
 rows={4}
 value={emailForm.body}
 onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
 placeholder="Edit email body..."
 />
 </Form.Group>
 </Col>
 </Row>
 </Form>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={onHide}>
 Cancel
 </Button>
 <Button variant="primary" onClick={handleSendEmail} disabled={sendingEmail}>
 {sendingEmail ? <Spinner size="sm" /> : "Send Email"}
 </Button>
 </Modal.Footer>
 </Modal>
 );
};

export default EmailModal;
